import { ChangeEvent, useEffect, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import IDashboardItem from "../../../models/IDashboardItem";
import styled from "styled-components";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import "./DashboardRemoteUrlWidget.scss";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import { Socket } from "socket.io-client";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    inEditor: boolean;
    widget?: IDashboardItem;
    removeWidget?: () => void;
    updateWidgetFn?: (widgetData: any, widgetUuid: string) => void;
};

enum RemoteUrlType {
    PDF = "PDF",
    VIDEO = "VIDEO",
    IMAGE = "IMAGE",
    URL = "URL",
}

export const DashboardRemoteUrlWidget = (props: Props) => {
    const [url, setUrl] = useState(props.widget?.widget_data?.url || "");
    const [remoteType, setRemoteType] = useState<RemoteUrlType>(props.widget?.widget_data?.remoteType || "");
    const [title, setTitle] = useState(props.widget?.widget_data?.title || "");
    const [linkTitle, setLinkTitle] = useState(props.widget?.widget_data?.linkTitle || "");

    useEffect(() => {
        if (props.widget && props.updateWidgetFn) {
            if (
                url !== props.widget.widget_data?.url ||
                remoteType !== props.widget.widget_data?.remoteType ||
                title !== props.widget.widget_data?.title ||
                linkTitle !== props.widget.widget_data?.linkTitle
            ) {
                const widgetData = {
                    url,
                    remoteType,
                    title,
                    linkTitle,
                };

                props.updateWidgetFn(widgetData, props.widget.uuid);
            }
        }
    }, [url, remoteType, title, linkTitle]);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        switch (name) {
            case "url":
                if (url !== value) {
                    setUrl(value);
                }
                break;

            case "type":
                setRemoteType(value);
                break;

            case "title":
                setTitle(value);
                break;

            case "linkTitle":
                setLinkTitle(value);
                break;
        }
    };

    const getRemotePDFViewer = () => {
        let height = "h-100";

        if (props.widget?.widget_data.title?.length) {
            height = "h-90";
        }

        return (
            <>
                {props.widget?.widget_data?.title ? <div className={"h-10"}>{props.widget?.widget_data?.title}</div> : null}
                <div className={height}>
                    <div className={"pdf-viewer"}>
                        {props.widget?.widget_data.url ? <iframe src={props.widget?.widget_data.url}></iframe> : null}
                    </div>
                </div>
            </>
        );
    };

    const getRemoteVideoViewer = () => {
        let height = "h-100";

        if (props.widget?.widget_data.title?.length) {
            height = "h-90";
        }

        return (
            <>
                {props.widget?.widget_data?.title ? <div className={"h-10"}>{props.widget?.widget_data?.title}</div> : null}
                <div className={height}>
                    <video controls height={"100%"} width={"100%"}>
                        <source src={props.widget?.widget_data.url} height={"100%"} width={"auto"} />
                        Sorry, your browser doesn&#39;t support embedded videos.
                    </video>
                </div>
            </>
        );
    };

    const getRemoteImageViewer = () => {
        let height = "h-100";

        if (props.widget?.widget_data.title?.length) {
            height = "h-90";
        }

        return (
            <>
                {props.widget?.widget_data?.title ? <div className={"h-10"}>{props.widget?.widget_data?.title}</div> : null}
                <div className={height + " d-flex align-items-center justify-content-center"}>
                    <img src={props.widget?.widget_data.url} height={"100%"} width={"auto"} />
                </div>
            </>
        );
    };

    const getRemoteUrlViewer = () => {
        let height = "h-100";

        if (props.widget?.widget_data.title?.length) {
            height = "h-90";
        }

        return (
            <>
                {props.widget?.widget_data?.title ? <div className={"h-10"}>{props.widget?.widget_data?.title}</div> : null}
                <div className={height + " d-flex align-items-center justify-content-center"}>
                    <a href={props.widget?.widget_data.url} target={"_blank"} rel="noreferrer">
                        {linkTitle || "Link"}
                    </a>
                </div>
            </>
        );
    };

    const getTypeViewer = () => {
        if (props.widget?.widget_data) {
            switch (props.widget.widget_data.remoteType.toUpperCase()) {
                case RemoteUrlType.PDF:
                    return getRemotePDFViewer();

                case RemoteUrlType.VIDEO:
                    return getRemoteVideoViewer();

                case RemoteUrlType.IMAGE:
                    return getRemoteImageViewer();

                case RemoteUrlType.URL:
                    return getRemoteUrlViewer();
            }
        } else {
            return null;
        }
    };

    return (
        <WidgetContainer className={"DashboardRemoteUrlWidget widget"}>
            {props.inEditor ? (
                <EditorContainer className={"w-100"}>
                    <div>
                        <OwIconButton
                            iconClass={"fas fa-trash-alt"}
                            iconColorClass={"text-danger"}
                            fn={() => {
                                props.removeWidget!();
                            }}
                        />
                    </div>
                    <div>
                        <OwLabel title={"remote_url"} />
                    </div>
                    <div className={"mt-2"}>
                        <OwLabel title={"title"} />
                    </div>
                    <div className={"w-100"} style={{ zIndex: 10 }}>
                        <OwFnInput fn={handleChange} name={"title"} value={title} enableDebounce={true} />
                    </div>
                    <div className={"mt-2"}>
                        <OwLabel title={"type"} />
                    </div>
                    <div>
                        <select className={"form-control form-control-sm"} name={"type"} value={remoteType} onChange={handleChange}>
                            <option></option>
                            <option value={RemoteUrlType.IMAGE} selected={remoteType === RemoteUrlType.IMAGE}>
                                Image
                            </option>
                            <option value={RemoteUrlType.VIDEO} selected={remoteType === RemoteUrlType.VIDEO}>
                                Video
                            </option>
                            <option value={RemoteUrlType.PDF} selected={remoteType === RemoteUrlType.PDF}>
                                PDF
                            </option>
                            <option value={RemoteUrlType.URL} selected={remoteType === RemoteUrlType.URL}>
                                URL / Link
                            </option>
                        </select>
                    </div>

                    <div className={"mt-2"}>
                        <OwLabel title={"url"} />
                    </div>
                    <div className={"w-100"} style={{ zIndex: 10 }}>
                        <OwFnInput fn={handleChange} name={"url"} value={url} enableDebounce={true} />
                    </div>

                    {remoteType === RemoteUrlType.URL ? (
                        <div>
                            <div className={"mt-2"}>
                                <OwLabel title={"link_title"} />
                            </div>
                            <div>
                                <OwFnInput fn={handleChange} name={"linkTitle"} value={linkTitle} enableDebounce={true} />
                            </div>
                        </div>
                    ) : null}
                </EditorContainer>
            ) : (
                <div className={"w-100 h-100"}>{getTypeViewer()}</div>
            )}
        </WidgetContainer>
    );
};

const WidgetContainer = styled.div`
    padding: 0.5rem;
    justify-content: start !important;
    align-items: start !important;
    height: 100%;

    input {
        width: 100%;
    }

    .position-relative {
        width: 100%;
    }
`;

const EditorContainer = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    overflow-y: scroll;
`;
